import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
/**
 * RecordReportDialog — section-picker for per-record customer / project
 * reports. Emits POST /api/reports/{kind}/{id}/generate with the chosen
 * sections + format (pdf | excel) and triggers a file download.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CUSTOMER_SECTIONS = [
  { id: 'general', label: i18n.t('auto.lbl.generalInfoNameEmailPhoneAddress') },
  { id: 'invoicing', label: i18n.t('auto.lbl.invoicingTaxIdVatInvoiceEmail') },
  { id: 'contacts', label: i18n.t('auto.lbl.contactsNamedContactPersons') },
  { id: 'attachments', label: i18n.t('auto.lbl.attachmentsList') },
  { id: 'notes', label: i18n.t('auto.lbl.notes') },
];
const PROJECT_SECTIONS = [
  { id: 'general', label: i18n.t('auto.lbl.generalInfoCodeNameDescriptionCustomer') },
  { id: 'address', label: i18n.t('auto.lbl.siteAddressStreetCityPostalCode') },
  { id: 'team', label: i18n.t('auto.lbl.teamMembersWithContactInfo') },
  { id: 'contacts', label: i18n.t('auto.lbl.projectContactsCustomerSiteSupervisors') },
  { id: 'financial', label: i18n.t('auto.lbl.financialBudgetRatesEstimatedHours') },
  { id: 'attachments', label: i18n.t('auto.lbl.attachmentsList') },
  { id: 'notes', label: i18n.t('auto.lbl.notes') },
];
const EMPLOYEE_SECTIONS = [
  { id: 'general', label: i18n.t('auto.lbl.generalInfoNameStatusDepartmentPosition') },
  { id: 'contact', label: i18n.t('auto.lbl.contactEmailPhoneAddressEmergencyContact') },
  { id: 'personal', label: i18n.t('auto.lbl.personalFirstMiddleLastNameBirth') },
  { id: 'identity', label: i18n.t('auto.lbl.identityDocumentsIdCardPassportDriving') },
  { id: 'employment', label: i18n.t('auto.lbl.employmentContractTypeWorkingHoursRates') },
  { id: 'positions', label: i18n.t('auto.lbl.positionsUpTo3WithRole') },
  { id: 'business', label: i18n.t('auto.lbl.businessNameAddressNipRegonPkd') },
  { id: 'certificates', label: i18n.t('auto.lbl.certificatesA1SepOshaLiftsCriminal') },
  { id: 'visas', label: i18n.t('auto.lbl.visasWorkPermitsEuUsUk') },
  { id: 'misc', label: i18n.t('auto.lbl.contractMiscContractNumberDateInvoice') },
  { id: 'current_projects', label: i18n.t('auto.lbl.currentUpcomingProjectsWithAssignmentWindow') },
  { id: 'attachments', label: i18n.t('auto.lbl.attachmentsListEmbeddedScansPdfs') },
  { id: 'notes', label: i18n.t('auto.lbl.notes') },
];
const COMPANY_SECTIONS = [
  { id: 'details', label: i18n.t('auto.lbl.companyDetailsNameAddressContactTax') },
  { id: 'banks', label: i18n.t('auto.lbl.bankAccountsPlnEurGbpUsd') },
  { id: 'representatives', label: i18n.t('auto.lbl.legalRepresentativesPositionPhoneEmailIds') },
];

const API = process.env.REACT_APP_BACKEND_URL;

const RecordReportDialog = ({ open, onOpenChange, kind, recordId, recordName }) => {
  const { t } = useTranslation();
  const sections = kind === 'customer' ? CUSTOMER_SECTIONS
    : kind === 'employee' ? EMPLOYEE_SECTIONS
    : kind === 'company' ? COMPANY_SECTIONS
    : PROJECT_SECTIONS;
  const [selected, setSelected] = useState(() => sections.map(s => s.id));
  const [busy, setBusy] = useState(null);

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const setAll = (all) => setSelected(all ? sections.map(s => s.id) : []);

  const generate = async (format) => {
    if (selected.length === 0) {
      toast.error(t('auto.misc2.pickAtLeastOneSection'));
      return;
    }
    setBusy(format);
    try {
      // Company is a singleton — no ID segment in the URL.
      const url = kind === 'company'
        ? `${API}/api/reports/company/generate`
        : `${API}/api/reports/${kind}/${recordId}/generate`;
      const r = await axios.post(
        url,
        { sections: selected, format },
        { responseType: 'blob' },
      );
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = weekFileName(`${kind}_${(recordName || 'report').replace(/\s+/g, '_')}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate report');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="record-report-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Print report — {recordName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2 text-xs">
            <button type="button" onClick={() => setAll(true)} className="text-slate-600 hover:text-slate-900 underline" data-testid="report-select-all">{t('auto.misc2.selectAll')}</button>
            <span className="text-slate-300">·</span>
            <button type="button" onClick={() => setAll(false)} className="text-slate-600 hover:text-slate-900 underline" data-testid="report-deselect-all">{t('auto.misc2.deselectAll')}</button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {sections.map(s => (
              <label key={s.id} className="flex items-start gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={selected.includes(s.id)}
                  onCheckedChange={() => toggle(s.id)}
                  data-testid={`report-section-${s.id}`}
                />
                <span className="text-sm">{s.label}</span>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter className="gap-2 flex flex-row sm:justify-end">
          <DocLangToggle testId="doc-lang-recordreportdialog-0" />
          <Button variant="outline" onClick={() => generate('excel')} disabled={busy !== null} data-testid="report-download-excel">
            {busy === 'excel' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />} Excel
          </Button>
          <Button onClick={() => generate('pdf')} disabled={busy !== null} data-testid="report-download-pdf">
            {busy === 'pdf' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />} PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecordReportDialog;
